import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Icon,
  Input,
  InputGroup,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  ArrowRightIcon,
  ArrowTrendingUpIcon,
  BookOpenIcon,
  DocumentIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  Square3Stack3DIcon,
  ViewfinderCircleIcon,
} from '@heroicons/react/24/outline';
import { useParams } from 'react-router-dom';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import api from '@/utils/api';
import { Content } from '@/lib/types';
import { slotFor } from '@/lib/slot-color';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (item: Content) => void;
};

// Semantic icon per content type. Colour comes from the DS tag-slot palette
// (slotFor) at the call site, so it groups by type name, not meaning.
function pickIcon(name: string | undefined): typeof ViewfinderCircleIcon {
  const n = (name || '').toLowerCase();
  if (n.includes('drill')) return ViewfinderCircleIcon;
  if (n.includes('skill')) return SparklesIcon;
  if (n.includes('play')) return ArrowTrendingUpIcon;
  if (n.includes('tactic')) return Square3Stack3DIcon;
  if (n.includes('document')) return DocumentIcon;
  if (n.includes('article') || n.includes('post')) return BookOpenIcon;
  return DocumentIcon;
}

export default function FindContentDialog({ isOpen, onClose, onSelect }: Props) {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState<Content[]>([]);
  const [recent, setRecent] = useState<Content[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset state when reopening, and load recents on first open.
  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setDebouncedQuery('');
    setResults([]);
    if (recent.length === 0 && workspaceId) {
      api
        .get(`contents/recent/${workspaceId}?limit=8`)
        .then((res) => setRecent(res.data.data || []))
        .catch(() => undefined);
    }
    // Focus the input after the dialog mounts.
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [isOpen, workspaceId, recent.length]);

  // Debounce the query (200ms).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  // Run search whenever the debounced query changes.
  useEffect(() => {
    if (!isOpen || !workspaceId) return;
    const q = debouncedQuery;
    if (!q) {
      setResults([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    let cancelled = false;
    api
      .get(`contents/search/${workspaceId}?q=${encodeURIComponent(q)}&limit=20`)
      .then((res) => {
        if (cancelled) return;
        setResults(res.data.data || []);
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, workspaceId, isOpen]);

  const showRecent = !debouncedQuery;
  const items = useMemo(
    () => (showRecent ? recent : results),
    [showRecent, recent, results],
  );

  const handleSelect = (item: Content) => {
    onClose();
    onSelect(item);
  };

  return (
    <DialogRoot lazyMount open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
      <DialogBackdrop />
      <DialogContent maxW='640px'>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>Find content</DialogTitle>
          <Text fontSize='sm' color='fg.muted' mt={1}>
            Search across every library and collection.
          </Text>
        </DialogHeader>
        <DialogBody pb={5}>
          <InputGroup
            startElement={<Icon as={MagnifyingGlassIcon} w={4} h={4} color='fg.subtle' />}
          >
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Search drills, skills, plays...'
              bg='bg.subtle'
              borderColor='border'
              borderRadius='lg'
              size='lg'
              fontSize='md'
            />
          </InputGroup>

          <Box mt={4}>
            <Text
              fontSize='xs'
              fontWeight='bold'
              letterSpacing='wider'
              color='fg.muted'
              mb={2}
            >
              {showRecent ? 'RECENTLY ADDED' : 'RESULTS'}
            </Text>

            {isLoading && (
              <Flex justify='center' py={6}>
                <Spinner size='sm' color='fg.subtle' />
              </Flex>
            )}

            {!isLoading && items.length === 0 && (
              <Box py={6} textAlign='center'>
                <Text fontSize='sm' color='fg.muted'>
                  {showRecent
                    ? 'No content yet — start by creating one.'
                    : `No matches for "${debouncedQuery}".`}
                </Text>
              </Box>
            )}

            {!isLoading && items.length > 0 && (
              <Stack gap={1}>
                {items.map((item) => {
                  const ChoiceIcon = pickIcon(item.content_type_name);
                  const slot = slotFor(item.content_type_name || '');
                  return (
                    <Flex
                      key={item.id}
                      as='button'
                      type='button'
                      onClick={() => handleSelect(item)}
                      align='center'
                      gap={3}
                      px={2}
                      py={2}
                      borderRadius='md'
                      bg='transparent'
                      width='100%'
                      textAlign='left'
                      transition='background 0.15s'
                      _hover={{ bg: 'bg.subtle' }}
                    >
                      <Flex
                        align='center'
                        justify='center'
                        w={9}
                        h={9}
                        borderRadius='md'
                        bg={`slot${slot}.bg`}
                        flexShrink={0}
                      >
                        <Icon as={ChoiceIcon} w={4} h={4} color={`slot${slot}.fg`} />
                      </Flex>
                      <Box flex='1' minW={0}>
                        <Text
                          fontSize='sm'
                          fontWeight='semibold'
                          color='fg.strong'
                          lineClamp={1}
                        >
                          {item.name || 'Untitled'}
                        </Text>
                        <Text fontSize='xs' color='fg.muted' lineClamp={1}>
                          {item.content_type_name || 'Content'}
                          {item.created_user && ` · ${item.created_user}`}
                        </Text>
                      </Box>
                      <Icon as={ArrowRightIcon} w={4} h={4} color='fg.subtle' flexShrink={0} />
                    </Flex>
                  );
                })}
              </Stack>
            )}
          </Box>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
}
